import { Control, Controller, PathValue } from 'react-hook-form';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  Paper,
  Popper,
  PopperProps,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import {
  Children,
  HTMLAttributes,
  forwardRef,
  useEffect,
  useState,
} from 'react';
import axios from 'axios';
import {
  CheckCircle,
  Close,
  MedicalInformation,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import {
  GenePanelCartPayload,
  GenePayload,
  SavedFilterResults,
} from '@/zod_schemas';
import { Gene } from '../types';
import { useListGenePanel } from '@/api/genes/genes';
import { getErrorMessage } from '@/utils/getErrorMessage';

// Define consistent column widths to ensure alignment
const gridTemplateColumns = '150px 150px';
// Custom Listbox component moved outside to prevent recreation on every render
const CustomListboxComponent = forwardRef<
  HTMLUListElement,
  HTMLAttributes<HTMLUListElement>
>((props, ref) => {
  const children = Children.toArray(props.children);

  return (
    <ul {...props} ref={ref} style={{ padding: 0, margin: 0, width: '100%' }}>
      {/* Table Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          alignItems: 'center',
          px: 2,
          py: 1,
          fontWeight: 'bold',
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
          width: '100%',
          '& > *': {
            padding: '0 2px',
          },
        }}
      >
        <Box sx={{ width: '100%' }}>Symbol</Box>
        <Box sx={{ width: '100%' }}>HGNC ID</Box>
      </Box>
      {/* Rows Container */}
      <Box sx={{ width: '100%' }}>{children}</Box>
    </ul>
  );
});
// Custom Popper component moved outside to prevent recreation on every render
const CustomPopperComponent = (props: PopperProps) => (
  <Popper
    {...props}
    style={{ width: 'auto', minWidth: '700px', zIndex: 1300 }}
    placement="bottom-start"
  />
);

interface GeneFilterProps {
  control: Control<SavedFilterResults>;
}

const GeneFilter = ({ control }: GeneFilterProps) => {
  const [genesData, setGenesData] = useState<GenePayload[]>([]);

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [maxList, setMaxList] = useState(25);
  const [totalResults, setTotalResults] = useState(0);
  const [selectedGenes] = useState<Gene[]>([]);
  const [selectedValue, setSelectedValue] = useState(null);

  const [viewDetails, setViewDetails] = useState<GenePanelCartPayload | null>(
    null,
  );

  const handleClose = () => setViewDetails(null);

  const { data } = useListGenePanel();

  const fetchGenes = async (query: string, max: number = 25) => {
    if (!query || query.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        'https://clinicaltables.nlm.nih.gov/api/genes/v4/search',
        {
          params: {
            terms: query,
            df: 'hgnc_id,symbol',
            maxList: max,
          },
        },
      );

      const [total, , , rows] = response.data;
      setTotalResults(total);

      const formatted = rows.map((item: string[]) => ({
        hgnc_id: item[0],
        symbol: item[1],
      }));

      const showMore = max === 25 && total > 25;
      const finalOptions = showMore
        ? [...formatted, { isFooter: true }]
        : formatted;

      setOptions(finalOptions);
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchGenes(inputValue, maxList);
    }, 300);
    return () => clearTimeout(delay);
  }, [inputValue, maxList]);

  const allGenesSymbol = [...new Set([...genesData.map((h) => h.symbol)])];
  const allGenePanels = [
    ...new Set([...data.results.map((h: GenePanelCartPayload) => h.name)]),
  ];

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
        <Typography>Gene Filters</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {/* Selected Genes Section */}

        <Controller
          name="request_filters.filters.gene_filters.include"
          control={control}
          defaultValue={
            allGenesSymbol as PathValue<
              SavedFilterResults,
              'request_filters.filters.gene_filters.include'
            >
          }
          render={({ field }) => {
            const selected: string[] = field.value ?? [];
            const { onChange } = field;

            const toggleGenes = (term: string) => {
              const updated = selected.includes(term)
                ? selected.filter((t) => t !== term)
                : [...selected, term];

              onChange(updated);
            };

            const areAllSelected = allGenesSymbol.every((id) =>
              selected.includes(id),
            );

            return (
              <FormControl fullWidth component="fieldset" sx={{ mb: 2 }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    backgroundColor: 'background.default',
                    maxHeight: 350,
                    overflowY: 'auto',
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ mb: 1, color: 'primary.main', fontWeight: 600 }}
                  >
                    Gene Search
                  </Typography>

                  <Autocomplete
                    size="small"
                    disablePortal
                    filterOptions={(x) => x}
                    options={options}
                    loading={loading}
                    value={selectedValue}
                    getOptionLabel={(option: GenePayload) =>
                      option.isFooter ? '' : option.symbol
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.hgnc_id === value.hgnc_id
                    }
                    onInputChange={(_, value) => {
                      setInputValue(value);
                      setMaxList(25);
                    }}
                    onChange={(_, newValue) => {
                      if (newValue && !newValue.isFooter) {
                        const alreadyExists = genesData.some(
                          (gene) => gene.symbol === newValue.symbol,
                        );

                        if (!alreadyExists) {
                          setGenesData((prev) => [...prev, newValue]);
                        }

                        const updatedSelected = [...selected, newValue.symbol];

                        setSelectedValue(null); // Clear the current selection
                        setInputValue(''); // Optionally clear input field
                        onChange(updatedSelected); // This updates the selected list
                      }
                    }}
                    onClose={() => {
                      // Additional cleanup when dropdown closes
                      //   setSelectedValue(null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Gene Symbol"
                        placeholder="e.g. BRCA1, TP53, EGFR"
                        variant="outlined"
                        helperText={
                          inputValue.length >= 2
                            ? `${totalResults} results found`
                            : 'Enter at least 2 characters to search'
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading && <CircularProgress size={20} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxComponent={CustomListboxComponent}
                    PopperComponent={CustomPopperComponent}
                    renderOption={(props, option) => {
                      if (option.isFooter) {
                        return (
                          <li
                            {...props}
                            style={{ justifyContent: 'center', padding: '8px' }}
                          >
                            <Typography
                              color="primary"
                              sx={{ cursor: 'pointer' }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setMaxList(500);
                              }}
                            >
                              See more items...
                            </Typography>
                          </li>
                        );
                      }

                      const isAlreadySelected = selectedGenes.some(
                        (g) => g.hgnc_id === option.hgnc_id,
                      );

                      return (
                        <li {...props} style={{ padding: 0, width: '100%' }}>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns,
                              px: 2,
                              py: 1,
                              columnGap: 0,
                              width: '100%',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              bgcolor: isAlreadySelected
                                ? 'action.selected'
                                : 'transparent',
                              '&:hover': {
                                bgcolor: isAlreadySelected
                                  ? 'action.focus'
                                  : 'action.hover',
                              },
                              '& > *': {
                                padding: '0 2px',
                              },
                            }}
                          >
                            <Box sx={{ width: '100%' }}>
                              <Typography noWrap fontWeight="bold">
                                {option.symbol}
                                {isAlreadySelected && (
                                  <Chip
                                    label="Added"
                                    size="small"
                                    color="primary"
                                    sx={{
                                      ml: 1,
                                      height: 18,
                                      fontSize: '0.65rem',
                                    }}
                                  />
                                )}
                              </Typography>
                            </Box>
                            <Box sx={{ width: '100%' }}>
                              <Typography noWrap>{option.hgnc_id}</Typography>
                            </Box>
                          </Box>
                        </li>
                      );
                    }}
                    renderTags={() => null}
                    sx={{ width: '100%', mb: 2 }}
                  />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      Genes
                    </Typography>
                    {genesData.length !== 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          onChange(areAllSelected ? [] : [...allGenesSymbol])
                        }
                      >
                        {areAllSelected ? 'Deselect All' : 'Select All'}
                      </Button>
                    )}
                  </Stack>

                  <Divider sx={{ mb: 2 }} />

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    {genesData.length === 0 ? (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        No genes selected. Use the search above to find and add
                        genes to your filter.
                      </Alert>
                    ) : (
                      genesData.map((gene) => {
                        const isSelected = selected.includes(gene.symbol);

                        return (
                          <Tooltip
                            key={gene.symbol}
                            title="Click to select/deselect. Right-click for details."
                            arrow
                          >
                            <Chip
                              label={gene.symbol}
                              variant={isSelected ? 'filled' : 'outlined'}
                              color={isSelected ? 'primary' : 'default'}
                              onClick={() => toggleGenes(gene.symbol)}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                // Add viewDetails logic
                              }}
                              icon={
                                isSelected ? (
                                  <CheckCircle fontSize="small" />
                                ) : (
                                  <RadioButtonUnchecked fontSize="small" />
                                )
                              }
                              sx={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        );
                      })
                    )}
                  </Box>
                </Paper>
              </FormControl>
            );
          }}
        />

        <Controller
          name="request_filters.filters.gene_panel_ids"
          control={control}
          defaultValue={
            allGenePanels as PathValue<
              SavedFilterResults,
              'request_filters.filters.gene_panel_ids'
            >
          }
          render={({ field }) => {
            const selected: string[] = field.value ?? [];
            const { onChange } = field;

            const toggleGenePanel = (term: string) => {
              const updated = selected.includes(term)
                ? selected.filter((t) => t !== term)
                : [...selected, term];
              onChange(updated);
            };

            const areAllSelected = allGenePanels.every((id) =>
              selected.includes(id!),
            );

            return (
              <FormControl fullWidth component="fieldset" sx={{ mb: 2 }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    backgroundColor: 'background.default',
                    maxHeight: 350,
                    overflowY: 'auto',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      Gene Panel
                    </Typography>

                    {data.results.length !== 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          onChange(areAllSelected ? [] : [...allGenePanels])
                        }
                      >
                        {areAllSelected ? 'Deselect All' : 'Select All'}
                      </Button>
                    )}
                  </Stack>

                  <Divider sx={{ mb: 2 }} />

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    {data.results.length === 0 ? (
                      <Alert severity="info" sx={{ mt: 2, width: '100%' }}>
                        Please create a panel to use this feature.(LINK TO
                        CREATE PANEL)
                      </Alert>
                    ) : (
                      data.results.map((panel: GenePanelCartPayload) => {
                        const isSelected = selected.includes(panel.name);

                        return (
                          <Tooltip
                            key={panel.name}
                            title="Click to select/deselect. Right-click for details."
                            arrow
                          >
                            <Chip
                              label={panel.name}
                              variant={isSelected ? 'filled' : 'outlined'}
                              color={isSelected ? 'primary' : 'default'}
                              onClick={() => toggleGenePanel(panel.name)}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                setViewDetails(panel);
                              }}
                              icon={
                                isSelected ? (
                                  <CheckCircle fontSize="small" />
                                ) : (
                                  <RadioButtonUnchecked fontSize="small" />
                                )
                              }
                              sx={{
                                // border: isNew ? '1px dashed orange' : undefined,
                                broder: '1px dashed orange',
                                cursor: 'pointer',
                              }}
                            />
                          </Tooltip>
                        );
                      })
                    )}
                  </Box>
                </Paper>
              </FormControl>
            );
          }}
        />
        {/* Detail dialog */}
        {viewDetails && (
          <Dialog
            open={Boolean(viewDetails)}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontWeight: 'bold',
                textAlign: 'center',
                fontSize: '1.2rem', // Reduces title font size
                py: 1.5, // Adjusts vertical padding (less height)
                position: 'relative',
              }}
            >
              <MedicalInformation sx={{ verticalAlign: 'middle', mr: 1 }} />
              <strong>Panel </strong>
              {viewDetails ? viewDetails.name : 'HPO Details'}
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: 'primary.contrastText',
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>

            <DialogContent
              sx={{
                bgcolor: 'background.default',
                p: 3,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {viewDetails && (
                <Paper
                  elevation={3}
                  sx={{
                    marginTop: 3,
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                    width: '100%',
                    maxWidth: 500, // Prevents content from stretching too much
                    textAlign: 'center', // Centers text inside Paper
                  }}
                >
                  <Typography
                    variant="h6"
                    color="primary"
                    sx={{ fontWeight: 'bold' }}
                  >
                    {viewDetails.name}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={2}>
                    {!viewDetails?.genes?.length ? (
                      <Alert severity="info" sx={{ mt: 2, width: '100%' }}>
                        No gene associated with this panel.
                      </Alert>
                    ) : (
                      <Typography>
                        <strong>HGNC ID:</strong>
                        {viewDetails.genes.map((g, i) => (
                          <span key={g.id}>
                            {g.gene.hgnc_id} (<strong>{g.gene.symbol}</strong>)
                            {viewDetails.genes &&
                              i < viewDetails.genes.length - 1 &&
                              ', '}
                          </span>
                        ))}
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              )}
            </DialogContent>
          </Dialog>
        )}

        {/* Panel Section */}
      </AccordionDetails>
    </Accordion>
  );
};
export default GeneFilter;
